import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Always use relative URLs - frontend and backend are on the same domain
  const backendUrl = '';
  
  // Log for debugging
  useEffect(() => {
    console.log('AuthContext: Using relative URLs');
    console.log('AuthContext: Current hostname:', window.location.hostname);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      console.log('AuthContext: Checking auth...');
      
      // Don't set loading to true if we already have a user
      if (!user) {
        setLoading(true);
      }
      
      const response = await fetch(`${backendUrl}/api/auth/check`, {
        credentials: 'include',
      });
      
      console.log('AuthContext: Auth check response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('AuthContext: Auth check response:', data);
        
        if (data.authenticated && data.user) {
          console.log('AuthContext: User is authenticated:', data.user.email);
          setUser(data.user);
          setError(null);
        } else {
          console.log('AuthContext: User is not authenticated');
          setUser(null);
        }
      } else {
        console.log('AuthContext: Auth check failed with status:', response.status);
        setUser(null);
      }
    } catch (err) {
      console.error('AuthContext: Auth check error:', err);
      setUser(null);
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  }, [backendUrl, user]);

  // Initial auth check on mount
  useEffect(() => {
    checkAuth();
  }, []); // Only run once on mount

  const login = () => {
    // Redirect to backend OIDC login endpoint
    console.log('AuthContext: Redirecting to login...');
    window.location.href = `${backendUrl}/api/auth/login`;
  };

  const logout = async () => {
    console.log('AuthContext: Logging out...');
    try {
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('AuthContext: Logout request failed:', err);
    }
    setUser(null);
    setAuthChecked(false);
    window.location.href = '/login';
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    authChecked,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
